/* -------------------
 * Color configuration
 * ------------------- */
import chroma from "chroma-js";
import {getStyleColorByVariable, sortObjectListByNumericKey} from "@/libs/Helpers";

export class LegendColorArtifact {
    constructor(colorFunction, minValue, maxValue, labelMinValue, labelMaxValue) {
        this.chromaJSColorFunction = colorFunction;

        this.minValue = minValue;
        this.maxValue = maxValue;

        this.labelMinValue = labelMinValue.toString();
        this.labelMaxValue = labelMaxValue.toString();
    }
}

class VariableColorConfigurationAbstract {
    /**
     * Class constructor
     * @param type {String} Type of color palette (continuous | discrete)
     * @param colors {Array} List of possible colors in palette
     * @param sort_key {String} Key to sort list of colors
     */
    constructor(type, colors, sort_key) {
        this.type = type;

        let variableColors = [];
        if (colors !== undefined && colors.length > 0)
            sortObjectListByNumericKey(colors, sort_key).forEach(colorItem => {
                variableColors.push(colorItem);
            });

        this.palette = variableColors;
    }

    get _colorConfigurationDefault() {
        return [
            getStyleColorByVariable('--layer-bg-config-level-1'),
            getStyleColorByVariable('--layer-bg-config-level-2'),
            getStyleColorByVariable('--layer-bg-config-level-3'),
        ];
    }

    getLegendColorArtifact(minValueData, maxValueData) {
        //Default colors
        let colorConfiguration = this._colorConfigurationDefault;
        let domainConfiguration = [minValueData, maxValueData];

        return {
            minValue: minValueData,
            maxValue: maxValueData,
            colorScaleFunction: chroma.scale(colorConfiguration).domain(domainConfiguration.sort())
        }
    }
}


class VariableColorConfigurationContinuous extends VariableColorConfigurationAbstract {
    /**
     * Class constructor
     * @param colors {Array} List of possible colors in palette
     */
    constructor(colors) {
        super('continuous', colors, 'value');
    }

    /**
     * Return LegendColorArtifact object with chroma.js function to calculate colors of legend for variable
     * @param minValueData Minimum value to use in case no minimum value is found
     * @param maxValueData Maximum value to use in case no minimum value is found
     * @returns {LegendColorArtifact} Object with function
     */
    getLegendColorArtifact(minValueData, maxValueData) {
        //Default colors
        let colorConfiguration = this._colorConfigurationDefault;

        //Keep list of float values for keys in color configuration
        let minValue = null;
        let maxValue = null;
        let domainConfiguration = [];

        //Sort values and set color list + domain of numbers
        if (this.palette.length > 0) {
            colorConfiguration = [];

            //Sort values
            this.palette.forEach((colorConfig) => {
                let colorItems = colorConfig.rgb;
                if (colorItems.length === 3) {
                    colorConfiguration.push(`rgb(${colorItems.join(', ')})`);
                    domainConfiguration.push(colorConfig.value);
                }
            });

            domainConfiguration.sort((a, b) => a - b);

            //Minimum and maximum values depend on range of colors
            minValue = domainConfiguration[0];
            maxValue = domainConfiguration.length > 1 ? domainConfiguration[domainConfiguration.length - 1] : minValue;
        }

        //Default values for minimum and maximum
        if (minValue === null || maxValue === null) {
            minValue = minValueData;
            maxValue = maxValueData;
            domainConfiguration = [minValue, maxValue];
        }

        domainConfiguration.sort((a, b) => a - b);
        let colorScaleFunction = chroma.scale(colorConfiguration).domain(domainConfiguration);

        return new LegendColorArtifact(colorScaleFunction, minValue, maxValue, minValue, maxValue);
    }
}

class VariableColorConfigurationDiscrete extends VariableColorConfigurationAbstract {
    /**
     * Class constructor
     * @param colors {Array} List of possible colors in palette
     */
    constructor(colors) {
        super('discrete', colors, 'min_value');
    }

    /**
     * Return LegendColorArtifact object with chroma.js function to calculate colors of legend for variable
     * @param minValueData Minimum value to use in case no minimum value is found
     * @param maxValueData Maximum value to use in case no minimum value is found
     * @returns {LegendColorArtifact} Object with function
     */
    getLegendColorArtifact(minValueData, maxValueData) {
        //Default colors
        let colorConfiguration = this._colorConfigurationDefault;

        let minValue = null;
        let maxValue = null;
        let labelMin = '';
        let labelMax = '';
        let domainConfiguration = [];

        //Sort values and set color list + domain of numbers
        if (this.palette.length > 0) {
            colorConfiguration = [];

            //Sort values
            this.palette.forEach((colorConfig) => {
                let colorItems = colorConfig.rgb;
                if (colorItems.length === 3) {
                    colorConfiguration.push(`rgb(${colorItems.join(', ')})`);

                    if (domainConfiguration.indexOf(colorConfig.min_value) === -1)
                        domainConfiguration.push(colorConfig.min_value);

                    if (domainConfiguration.indexOf(colorConfig.max_value) === -1)
                        domainConfiguration.push(colorConfig.max_value);
                }
            });

            domainConfiguration.sort((a, b) => a - b);

            //Minimum and maximum values depend on range of colors
            minValue = domainConfiguration[0];
            maxValue = domainConfiguration.length > 1 ? domainConfiguration[domainConfiguration.length - 1] : minValue;

            //Items are ordered by value
            labelMin = this.palette[0].label;
            labelMax = this.palette[this.palette.length - 1].label;
        }

        //Default values for minimum and maximum
        if (minValue === null || maxValue === null) {
            minValue = minValueData;
            maxValue = maxValueData;
            domainConfiguration = [minValue, maxValue];
        }

        let colorScaleFunction = chroma.scale(colorConfiguration).classes(domainConfiguration);

        return new LegendColorArtifact(colorScaleFunction, minValue, maxValue, labelMin, labelMax);
    }
}


export class VariableColorConfigurationInterface {
    /**
     * Class constructor
     * @param type {String} Type of color palette (continuous | discrete)
     * @param colors {Array} List of possible colors in palette
     */
    newObject(type, colors) {
        let objectNew;

        switch (type) {
            case 'discrete':
                objectNew = new VariableColorConfigurationDiscrete(colors);
                break;
            case 'continuous':
            default:
                objectNew = new VariableColorConfigurationContinuous(colors);
                break;
        }
        return objectNew;
    }
}